/**
 * Utility to translate Haitian Creole error messages returned by the backend
 * into the current active language (ht, fr, en).
 */

export const translateBackendError = (
    message: string | undefined | null,
    currentLang: string
): string => {
    if (!message) return "";

    // Extract primary language code (e.g. 'fr' from 'fr-FR' or 'ht' from 'ht')
    const lang = currentLang ? currentLang.split("-")[0].toLowerCase() : "ht";

    // 1. Account temporarily locked with remaining minutes
    // e.g. "Kont vewouye. Eseye ankò nan 15 minit."
    const lockMatch = message.match(/Kont vewouye\.\s+Eseye ankò nan\s+(\d+)\s+minit\./i);
    if (lockMatch) {
        const minutes = lockMatch[1];
        if (lang === "fr") {
            return `Compte verrouillé. Réessayez dans ${minutes} minute${parseInt(minutes) > 1 ? "s" : ""}.`;
        }
        if (lang === "en") {
            return `Account locked. Try again in ${minutes} minute${parseInt(minutes) > 1 ? "s" : ""}.`;
        }
        return `Kont bloke. Eseye ankò nan ${minutes} minit.`; // Creole fallback / normalized
    }

    // 2. PIN incorrect with warning about lock and remaining attempts
    // e.g. "PIN pa kòrèk. Kont vewouye pou 15 minit. 2 tantativ rete anvan blokaj definitif."
    const pinLockedMatch = message.match(/PIN pa kòrèk\.\s+Kont vewouye pou 15 minit\.\s+(\d+)\s+tantativ rete anvan blokaj definitif\./i);
    if (pinLockedMatch) {
        const attempts = pinLockedMatch[1];
        if (lang === "fr") {
            return `Code PIN incorrect. Compte verrouillé pendant 15 minutes. Il reste ${attempts} tentative${parseInt(attempts) > 1 ? "s" : ""} avant blocage définitif.`;
        }
        if (lang === "en") {
            return `Incorrect PIN. Account locked for 15 minutes. ${attempts} attempt${parseInt(attempts) > 1 ? "s" : ""} remaining before permanent lock.`;
        }
        return `PIN pa kòrèk. Kont bloke pou 15 minit. ${attempts} tantativ rete anvan blokaj definitif.`;
    }

    // 3. PIN incorrect with attempts remaining before temporary lock
    // e.g. "PIN pa kòrèk. 2 tantativ rete anvan vewouye tanporè."
    const pinAttemptsMatch = message.match(/PIN pa kòrèk\.\s+(\d+)\s+tantativ rete anvan vewouye tanporè\./i);
    if (pinAttemptsMatch) {
        const attempts = pinAttemptsMatch[1];
        if (lang === "fr") {
            return `Code PIN incorrect. Il reste ${attempts} tentative${parseInt(attempts) > 1 ? "s" : ""} avant verrouillage temporaire.`;
        }
        if (lang === "en") {
            return `Incorrect PIN. ${attempts} attempt${parseInt(attempts) > 1 ? "s" : ""} remaining before temporary lockout.`;
        }
        return `PIN pa kòrèk. ${attempts} tantativ rete anvan blokaj tanporè.`;
    }

    const lowercaseMsg = message.toLowerCase();

    // 4. Too many attempts, permanently locked
    if (lowercaseMsg.includes("twòp tantativ. kont bloke definitivman") || lowercaseMsg.includes("kont bloke definitivman")) {
        if (lang === "fr") return "Trop de tentatives. Compte bloqué définitivement. Contactez le service clientèle.";
        if (lang === "en") return "Too many attempts. Account permanently locked. Contact customer service.";
        return "Twòp tantativ. Kont bloke definitivman. Kontakte sèvis kliyan.";
    }

    // 5. User not found
    if (lowercaseMsg.includes("itilizatè pa jwenn")) {
        if (lang === "fr") return "Utilisateur introuvable.";
        if (lang === "en") return "User not found.";
        return "Itilizatè pa jwenn.";
    }

    // 6. Device not found
    if (lowercaseMsg.includes("aparèy pa jwenn")) {
        if (lang === "fr") return "Appareil introuvable.";
        if (lang === "en") return "Device not found.";
        return "Aparèy pa jwenn.";
    }

    // 7. Device not trusted
    if (lowercaseMsg.includes("aparèy sa poko konfye") || lowercaseMsg.includes("verifye li anvan")) {
        if (lang === "fr") return "Cet appareil n'est pas encore approuvé. Veuillez le vérifier d'abord.";
        if (lang === "en") return "This device is not trusted yet. Please verify it first.";
        return "Aparèy sa poko konfye. Verifye li anvan.";
    }

    // 8. OTP expired
    if (lowercaseMsg.includes("kòd otp ekspire")) {
        if (lang === "fr") return "Code OTP expiré. Demandez un nouveau code.";
        if (lang === "en") return "OTP code expired. Please request a new code.";
        return "Kòd OTP ekspire. Mande yon nouvo kòd.";
    }

    // 9. Too many OTP attempts
    if (lowercaseMsg.includes("twòp tantativ. mande yon nouvo kòd")) {
        if (lang === "fr") return "Trop de tentatives. Demandez un nouveau code.";
        if (lang === "en") return "Too many attempts. Please request a new code.";
        return "Twòp tantativ. Mande yon nouvo kòd.";
    }

    // 10. Incorrect OTP
    if (lowercaseMsg.includes("kòd otp pa kòrèk") || lowercaseMsg.includes("otp envalid")) {
        if (lang === "fr") return "Code OTP incorrect.";
        if (lang === "en") return "Incorrect OTP code.";
        return "Kòd OTP pa kòrèk.";
    }

    // 11. 2FA not enabled
    if (lowercaseMsg.includes("2fa pa aktive")) {
        if (lang === "fr") return "La double authentification (2FA) n'est pas activée sur ce compte.";
        if (lang === "en") return "Two-factor authentication (2FA) is not enabled on this account.";
        return "2FA pa aktive sou kont sa a.";
    }

    // 12. Invalid 2FA code
    if (lowercaseMsg.includes("kòd 2fa envalid") || lowercaseMsg.includes("kòd 2fa enposib")) {
        if (lang === "fr") return "Code 2FA invalide. Réessayez.";
        if (lang === "en") return "Invalid 2FA code. Please try again.";
        return "Kòd 2FA envalid. Eseye ankò.";
    }

    // 13. Email already in use
    if (lowercaseMsg.includes("imel sa deja itilize") || lowercaseMsg.includes("imèl sa a deja itilize") || lowercaseMsg.includes("imel sa a deja itilize") || lowercaseMsg.includes("email sa deja egziste")) {
        if (lang === "fr") return "Cet e-mail est déjà utilisé.";
        if (lang === "en") return "This email is already in use.";
        return "Imèl sa a deja itilize.";
    }

    // 14. Phone already in use
    if (lowercaseMsg.includes("nimewo telefòn sa deja itilize") || lowercaseMsg.includes("telefòn sa a deja itilize")) {
        if (lang === "fr") return "Ce numéro de téléphone est déjà utilisé.";
        if (lang === "en") return "This phone number is already in use.";
        return "Nimewo telefòn sa a deja itilize.";
    }

    // 15. Username already in use
    if (lowercaseMsg.includes("non itilizatè sa deja itilize") || lowercaseMsg.includes("non itilizatè sa a deja itilize") || lowercaseMsg.includes("non itilizatè sa deja pran")) {
        if (lang === "fr") return "Ce nom d'utilisateur est déjà utilisé.";
        if (lang === "en") return "This username is already in use.";
        return "Non itilizatè sa a deja itilize.";
    }

    // 16. PIN must be 4-6 digits
    if (lowercaseMsg.includes("pin dwe genyen 4-6 chif")) {
        if (lang === "fr") return "Le code PIN doit contenir entre 4 et 6 chiffres.";
        if (lang === "en") return "PIN must be between 4 and 6 digits.";
        return "PIN dwe genyen 4-6 chif.";
    }

    // 17. PIN already configured
    if (lowercaseMsg.includes("pin deja konfigire")) {
        if (lang === "fr") return "Le code PIN est déjà configuré pour ce compte.";
        if (lang === "en") return "PIN is already configured for this account.";
        return "PIN deja konfigire pou kont sa a.";
    }

    // 18. Token or userId invalid/expired
    if (lowercaseMsg.includes("token oswa userid invalide")) {
        if (lang === "fr") return "Jeton ou ID utilisateur invalide ou expiré.";
        if (lang === "en") return "Token or user ID is invalid or expired.";
        return "Jeton oswa ID itilizatè envalid oswa ekspire.";
    }

    // 19. Current PIN incorrect
    if (lowercaseMsg.includes("pin aktyèl pa kòrèk")) {
        if (lang === "fr") return "Le code PIN actuel est incorrect.";
        if (lang === "en") return "Current PIN is incorrect.";
        return "PIN aktyèl la pa kòrèk.";
    }

    // 20. New PINs do not match
    if (lowercaseMsg.includes("nouvo pin yo pa menm")) {
        if (lang === "fr") return "Les nouveaux codes PIN ne correspondent pas.";
        if (lang === "en") return "The new PINs do not match.";
        return "Nouvo PIN yo pa menm.";
    }

    // 21. User with this identifier doesn't exist
    if (lowercaseMsg.includes("itilizatè ak idantifyan sa a pa egziste")) {
        if (lang === "fr") return "Aucun utilisateur n'existe avec cet identifiant.";
        if (lang === "en") return "No user exists with this identifier.";
        return "Itilizatè ak idantifyan sa a pa egziste.";
    }

    // 22. Token invalid/expired
    if (lowercaseMsg.includes("token pa valid oswa li ekspire") || lowercaseMsg.includes("token envalid oswa ekspire")) {
        if (lang === "fr") return "Jeton invalide ou expiré.";
        if (lang === "en") return "Token is invalid or expired.";
        return "Jeton an pa valid oswa li ekspire.";
    }

    // 23. Token expired, ask for new link
    if (lowercaseMsg.includes("token ekspire. mande yon nouvo lyen") || lowercaseMsg.includes("token ekspire. rekòmanse")) {
        if (lang === "fr") return "Le jeton a expiré. Veuillez demander un nouveau lien.";
        if (lang === "en") return "Token expired. Please request a new link.";
        return "Jeton ekspire. Rekòmanse pwosesis la.";
    }

    // 24. Link invalid/expired
    if (lowercaseMsg.includes("lyen sa a pa valid oswa li ekspire") || lowercaseMsg.includes("link envalid oswa ekspire")) {
        if (lang === "fr") return "Ce lien n'est pas valide ou a expiré.";
        if (lang === "en") return "This link is invalid or expired.";
        return "Lyen sa a pa valid oswa li ekspire.";
    }

    // 25. User account invalid
    if (lowercaseMsg.includes("konto itilizatè a pa valid") || lowercaseMsg.includes("kont itilizatè a pa valid")) {
        if (lang === "fr") return "Le compte utilisateur est invalide.";
        if (lang === "en") return "User account is invalid.";
        return "Kont itilizatè a pa valid.";
    }

    // 26. Device already verified
    if (lowercaseMsg.includes("aparèy sa deja verifye")) {
        if (lang === "fr") return "Cet appareil a déjà été vérifié.";
        if (lang === "en") return "This device has already been verified.";
        return "Aparèy sa a deja verifye.";
    }

    // 27. Different security questions required
    if (lowercaseMsg.includes("chwazi 3 kesyon diferan")) {
        if (lang === "fr") return "Vous devez choisir 3 questions de sécurité différentes.";
        if (lang === "en") return "You must choose 3 different security questions.";
        return "Ou dwe chwazi 3 kesyon diferan.";
    }

    // 28. One or more questions invalid
    if (lowercaseMsg.includes("youn oswa plis kesyon pa valid")) {
        if (lang === "fr") return "Une ou plusieurs questions ne sont pas valides.";
        if (lang === "en") return "One or more questions are invalid.";
        return "Youn oswa plis kesyon pa valid.";
    }

    // 29. Solidaire plan only for BS accounts
    if (lowercaseMsg.includes("solidaire sèlman pou kont bs")) {
        if (lang === "fr") return "Épargne solidaire uniquement disponible pour les comptes BS.";
        if (lang === "en") return "Solidarity savings only available for BS accounts.";
        return "Solidaire sèlman pou kont BS.";
    }

    // 30. Account type doesn't match
    if (lowercaseMsg.includes("kalite kont lan pa koresponn")) {
        if (lang === "fr") return "Le type de compte ne correspond pas.";
        if (lang === "en") return "Account type does not match.";
        return "Kalite kont lan pa koresponn.";
    }

    // 31. Account has no solidarity plan active
    if (lowercaseMsg.includes("pa gen okenn plan solidè ki aktive")) {
        if (lang === "fr") return "Ce compte n'a aucun plan de solidarité actif.";
        if (lang === "en") return "This account has no active solidarity plan.";
        return "Kont sa a pa gen okenn plan solidè ki aktive.";
    }

    // 32. Agent not found
    if (lowercaseMsg.includes("agent non trouvé") || lowercaseMsg.includes("ajan pa jwenn")) {
        if (lang === "fr") return "Agent introuvable.";
        if (lang === "en") return "Agent not found.";
        return "Ajan pa jwenn.";
    }

    // 33. Unauthorized / Forbidden
    if (lowercaseMsg.includes("otantifikasyon obligatwa")) {
        if (lang === "fr") return "Authentification obligatoire.";
        if (lang === "en") return "Authentication required.";
        return "Otantifikasyon obligatwa.";
    }

    if (lowercaseMsg.includes("ou pa gen otorizasyon pou aksè sa")) {
        if (lang === "fr") return "Vous n'avez pas l'autorisation pour cet accès.";
        if (lang === "en") return "You do not have authorization for this access.";
        return "Ou pa gen otorizasyon pou aksè sa a.";
    }

    // 34. Account frozen
    if (lowercaseMsg.includes("kont sa deja frizye")) {
        if (lang === "fr") return "Ce compte est déjà gelé.";
        if (lang === "en") return "This account is already frozen.";
        return "Kont sa a deja friz.";
    }

    // 35. Account not frozen
    if (lowercaseMsg.includes("kont sa pa frize")) {
        if (lang === "fr") return "Ce compte n'est pas gelé.";
        if (lang === "en") return "This account is not frozen.";
        return "Kont sa a pa friz.";
    }

    // 36. Service key already exists
    if (lowercaseMsg.includes("sèvis ak kle sa deja egziste")) {
        if (lang === "fr") return "Un service avec cette clé existe déjà.";
        if (lang === "en") return "A service with this key already exists.";
        return "Sèvis ak kle sa a deja egziste.";
    }

    return message;
};
